import { Component, input } from '@angular/core';
import { ButtonComponent } from '../../../../components/button/button.component';
import { IconComponent } from '../../../../components/icon/icon.component';
import { DownloadUtil } from '../../../../utils/download/download';
import { calcAverage, encapsulateString, extractUserObject } from './feedback-util';
import { mapFeedbackId, mapAnswerId } from '../idMappers';
import { FeatureType } from '@shared/interfaces/feedback';

interface FeedbackData {
  [x: string]: string | number | boolean | string[];
}
@Component({
  selector: 'app-feedback-download',
  imports: [ButtonComponent, IconComponent],
  templateUrl: './feedback-download.component.html',
  styleUrl: './feedback-download.component.css',
})
export class FeedbackDownloadComponent {
  public data = input.required<FeedbackData[]>();
  public feature = input.required<FeatureType>();

  protected downloadCSV() {
    const data = this.data();

    // Setting headers (we loop though the data to get all possible headers)
    const questionIds = new Set<string>();
    const fixedHeaders = ['userEmail', 'createdAt', 'consent', 'context', 'rating'];
    data.forEach((item) => {
      Object.keys(item).forEach((q) => {
        if (!fixedHeaders.includes(q)) {
          questionIds.add(q);
        }
      });
    });

    // Extract user data
    const userEmail = data[0]?.['userEmail'];
    let userData = {};
    if (userEmail) {
      userData = extractUserObject(userEmail as string[]);
    }

    // Combine all headers
    const mappedQuestions = new Array(...questionIds).map((q) => mapFeedbackId(this.feature(), q));
    const headers = [...Object.keys(userData), ...fixedHeaders.slice(1), ...mappedQuestions];

    // Loop through the data and add all the values to the rows (or leave empty if not present)
    const rows = data.map((item) => {
      const answers = Array.from(questionIds).map((id) => {
        const answerIds = item[id];
        if (Array.isArray(answerIds)) {
          return mapAnswerId(this.feature(), id, answerIds as string[]).join(', ');
        }
        return answerIds ? answerIds.toString() : '';
      });

      const customValues = fixedHeaders.slice(1).map((v) => item[v].toString());
      return [...(Object.values(userData) as string[]), ...customValues, ...answers].map((v) => encapsulateString(v));
    });

    // Remove rating if it is -1 (value used as a placeholder)
    const ratingIndex = Array.from(headers).indexOf('rating');
    const ratingAverage = calcAverage(rows, ratingIndex);
    if (ratingAverage === -1) {
      headers.splice(ratingIndex, 1);
      rows.forEach((row) => row.splice(ratingIndex, 1));
    }

    // Encapsulate the headers in double quotes and join them with commas
    const headersString = headers.map((header) => `"${header.replace(/"/g, '""')}"`).join(',');

    const csvContent = [headersString, ...rows.map((row) => row.join(','))].join('\n');
    DownloadUtil.downloadFile(new Blob([csvContent], { type: 'text/csv' }), 'feedback.csv');
  }
}
